'use client';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { SEO } from '../../components/common/SEO';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Clock, Mail } from 'lucide-react';
import { TermsAndConditionsPopup } from '../../components/modals/TermsAndConditionsPopup';
import { PrivacyPolicyPopup } from '../../components/modals/PrivacyPolicyPopup';
import BiglocalLoader from '../../components/common/BigLocalLoader';
import { URLS } from '../../constant/constant';

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="green">
    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.371-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01s-.521.074-.792.372c-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
  </svg>
);

const EmailIcon = () => (
  <div className="flex-shrink-0 size-12 bg-white rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
    <Mail className="w-6 h-6 text-blue-600" />
  </div>
);

export default function OTPPage() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(30);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

  const navigate = useNavigate();
  const {
    verifyOTP,
    verifyEmailOTP,
    phoneNumber,
    emailAddress,
    loginMethod,
    setPhoneNumber,
    loading,
    initiateLogin,
    initiateLoginByEmail,
    handleSuccessfulAuth,
  } = useAuth();

  const identifier = loginMethod === 'email' ? emailAddress : phoneNumber;

  useEffect(() => {
    if (!identifier) {
      navigate('/login');
    }
  }, [identifier, navigate]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChange = (value) => {
    setError('');
    const cleanValue = value.replace(/\D/g, '').slice(0, 6);
    setOtp(cleanValue);

    if (cleanValue.length === 6) {
      setTimeout(() => {
        handleSubmit({ preventDefault: () => {} }, cleanValue);
      }, 100);
    }
  };

  const handleKeyDown = (e) => {
    if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e, otpToSubmit) => {
    e.preventDefault();
    const otpValue = otpToSubmit || otp;
    if (otpValue.length !== 6) {
      setError('Please enter complete OTP');
      return;
    }

    let data;
    if (loginMethod === 'email') {
      data = await verifyEmailOTP(otpValue, emailAddress);
    } else {
      setPhoneNumber(phoneNumber);
      data = await verifyOTP(otpValue, phoneNumber);
    }

    if (data) {
      await handleSuccessfulAuth(data, identifier);
    } else {
      setError('Failed to verify OTP. Please try again.');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleResendOTP = async () => {
    setOtp('');
    let res;
    if (loginMethod === 'email') {
      res = await initiateLoginByEmail(emailAddress);
    } else {
      res = await initiateLogin(phoneNumber);
    }
    if (res) {
      setTimer(30);
      setError('');
    }
  };

  return (
    <div className="min-h-screen w-full bg-slate-50 antialiased">
      <SEO
        title="Verify - BigLocal"
        description="Enter the verification code to access the BigLocal marketplace."
        canonicalUrl={`${URLS.USER}/auth/otp`}
      />

      {loading && <BiglocalLoader title="Veryfying the OTP" />}

      <div className="w-full max-w-8xl mx-auto bg-white shadow-xl overflow-hidden md:flex h-screen">
        {/* Left Panel - Illustration (Desktop) */}
        <div className="hidden md:flex md:w-1/2 bg-white items-center justify-center p-8">
          <div className="w-full max-w-md">
            <img
              src="/images/login/banner1.webp"
              alt="Illustration of local services"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="w-full md:w-1/2 flex flex-col justify-center">
          <div className="p-6 sm:p-8 w-full max-w-md mx-auto">
            <div className="text-center mb-6">
              <img
                src="/images/biglocallogo3.webp"
                alt="Biglocal Logo"
                className="w-28 mx-auto mb-4"
              />
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">
                Enter Verification Code
              </h1>
              <p className="text-gray-600 mt-2">
                Sent to{' '}
                <span className="font-bold text-gray-800">
                  {loginMethod === 'email' ? emailAddress : `+91 ${phoneNumber}`}{' '}
                  <button
                    onClick={() =>
                      navigate('/login', {
                        state: {
                          isEdit: true,
                          phoneNumber: loginMethod === 'phone' ? phoneNumber : undefined,
                        },
                      })
                    }
                    className="text-blue-600 font-medium hover:underline"
                  >
                    edit
                  </button>
                </span>
              </p>
            </div>

            <div className="w-full rounded-xl bg-blue-50 p-4 text-center border border-blue-100 mb-6 flex items-center gap-4">
              {loginMethod === 'email' ? (
                <>
                  <EmailIcon />
                  <div>
                    <h2 className="text-sm font-bold text-gray-800 text-left">Code sent to Email</h2>
                    <p className="text-xs text-gray-600 text-left">
                      Please check your inbox (and spam folder) for the code.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-shrink-0 size-12 bg-white rounded-full flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <WhatsAppIcon />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-800 text-left">Code sent to WhatsApp</h2>
                    <p className="text-xs text-gray-600 text-left">
                      Please check your messages to get the code.
                    </p>
                  </div>
                </>
              )}
            </div>

            <form onSubmit={(e) => handleSubmit(e, otp)} className="space-y-4">
              <input
                type="tel"
                maxLength={6}
                value={otp}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                className={`w-full h-12 text-center text-xl font-bold rounded-md border transition-all duration-300  placeholder:text-gray-300 ${
                  error
                    ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500'
                    : otp.length === 6
                      ? 'border-blue-400 bg-blue-50 text-blue-700 focus:border-blue-500'
                      : 'border-gray-300 bg-white focus:border-blue-500'
                } focus:outline-none focus:ring-1 focus:ring-blue-100`}
              />

              {error && (
                <div className="flex items-center justify-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full !mt-6 h-12 text-lg font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Verify & Continue'}
              </Button>

              <div className="text-center pt-2">
                {timer > 0 ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Resend code in {timer}s</span>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    onClick={handleResendOTP}
                    className="text-blue-600 hover:text-blue-700 font-bold"
                  >
                    Resend Code
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <TermsAndConditionsPopup isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} />
      <PrivacyPolicyPopup isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} />
    </div>
  );
}
